import { AxiosResponse } from 'axios'
import {
  NormalizedResponse,
  BizResCode,
  NormalizedBizError
} from '@/types/axios/public-json-structure'
import { CustomizeInterceptor } from '@/types/axios/interceptor'
import { getJsonBlobContent } from '@/utils/get-json-blob-content'

/**
 * 归一化响应内容，并在相应内容中附加上原始axios响应
 * 响应内容：前后端经过协商定义好的、响应数据的JSON格式。如果后端响应为Blob，则前端对自行将响应数据组装成兼容这种JSON格式的对象，以便于TS类型检查
 * @param axiosRes axios响应对象
 * @returns
 */
export const normalizeResponse = (
  axiosRes: AxiosResponse
): NormalizedResponse => {
  if (axiosRes.data instanceof Blob || typeof axiosRes.data === 'string') {
    return {
      axiosRes,
      code: BizResCode.SUCCESS,
      data: axiosRes.data,
      message: '',
      page: null
    }
  }
  return {
    axiosRes,
    ...axiosRes.data
  }
}

/**
 * 归一化业务报错
 * 业务报错：HTTP 响应为 200，但后端判断业务逻辑发生错误
 * @param axiosRes axios响应对象
 * @returns
 */
export const normalizeBizError = (
  axiosRes: AxiosResponse
): NormalizedBizError => ({
  errorType: 'custom',
  code: axiosRes.data.code,
  axiosRes,
  message: axiosRes.data.message,
  messageHasShown: false
})

export const replaceBlobWithParsedJson = async (response: AxiosResponse) => {
  // 从 blob 中获取 JSON 信息，处理任何可能的因错误而导致的返回Blob的情况
  // Blob#text() 方法返回一个 Promise 对象, 包含 blob 中的内容  参考自: https://developer.mozilla.org/zh-CN/docs/Web/API/Blob/text
  // 低版本浏览器中不存在 Blob#text()，需要使用FileReader进行兼容处理
  let jsonFromBlob: any
  try {
    jsonFromBlob = await getJsonBlobContent(response.data)
    Object.defineProperty(jsonFromBlob, '_rawBlob', {
      enumerable: false,
      value: response.data
    })
    response.data = jsonFromBlob
  } catch (err) {
    console.error(
      '【normalize拦截器】解析Blob中的JSON时发生错误，值为：',
      response.data
    )
  }
}

const interceptor: CustomizeInterceptor<Promise<NormalizedResponse>, any> = {
  fulfilled: async function fulfilled(response: AxiosResponse) {
    const httpStatus = Number(response.status) || 200
    // 处理下载文件
    // TODO: 可以把这一步放到一个单独的响应拦截器中，避免此处代码臃肿
    /**
     * 解决由于 TS 限制接受文件类型为 blob, 导致当返回 JSON 格式后 axios 将其转换为 blob 格式
     * 禅道地址: http://10.0.3.18/pro/bug-view-65586.html
     */
    if (
      httpStatus === 200 &&
      response.config.responseType === 'blob' &&
      response.data.type === 'application/json'
    ) {
      // 如果响应为JSON，则直接替换整个response.data为这个JSON
      await replaceBlobWithParsedJson(response)
    }

    const normalizedRes = normalizeResponse(response)
    const code = normalizedRes.code
    if (httpStatus === 200 && code === BizResCode.SUCCESS) {
      // 业务请求成功
      return Promise.resolve(normalizedRes)
    }

    // 业务请求失败, 可能多种原因
    return Promise.reject(normalizeBizError(response))
  },
  rejected: undefined
}

export default interceptor
